import type { AxiosRequestConfig } from 'axios'

import type {
  LegacyRoadmapResponse,
  ProductUpdate,
  ProductUpdateDto,
  RoadmapItem,
} from '../models/whatsNew.types'

import { ensureCatalogEntry, invalidateCatalogEntry, type CatalogDeps } from '@/shared/catalog'
import { appHttp, unwrap } from '@/shared/http'

const AMBIENT_READ: AxiosRequestConfig = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
}

/** Manifest entry name = URL segment = cache key (contract 29 §6.6, amended 2026-08-04). */
const ROADMAP_CATALOG = 'product_roadmap'
const WEB_CHANGELOG_STREAM = 'web_changelog'
const MONTHLY_SUMMARY_TITLE = /^product updates\s*-\s*\S+\s+\d{4}$/i

interface RoadmapCatalogBody {
  items: RoadmapItem[]
}

function isRoadmapCatalogBody(body: unknown): body is RoadmapCatalogBody {
  return (
    typeof body === 'object' && body !== null && Array.isArray((body as { items?: unknown }).items)
  )
}

type LocalizedText = string | { en?: string | null; tr?: string | null } | null | undefined

function localized(value: LocalizedText): string {
  if (typeof value === 'string') return value
  return value?.en || value?.tr || ''
}

function itemsFrom(payload: unknown): ProductUpdateDto[] {
  if (Array.isArray(payload)) return payload as ProductUpdateDto[]
  if (typeof payload !== 'object' || payload === null) return []
  const wrapped = payload as { results?: unknown; data?: unknown }
  if (Array.isArray(wrapped.results)) return wrapped.results as ProductUpdateDto[]
  if (Array.isArray(wrapped.data)) return wrapped.data as ProductUpdateDto[]
  return []
}

function isVisibleWebUpdate(update: ProductUpdateDto): boolean {
  const published =
    update.is_published !== false && (!update.status || update.status === 'published')
  const title = localized(update.title).trim()
  const webStream = update.stream
    ? update.stream === WEB_CHANGELOG_STREAM
    : !MONTHLY_SUMMARY_TITLE.test(title)
  return published && webStream
}

function toEnglishProductUpdate(update: ProductUpdateDto): ProductUpdate {
  return {
    id: update.id,
    title: localized(update.title),
    description: localized(update.description),
    content: localized(update.content),
    slug_name: update.slug_name,
    created_at: update.created_at,
    published_at: update.published_at,
    status: update.status,
    is_published: update.is_published,
    media_type: update.media_type,
    media_url: update.media_url || null,
  }
}

export class WhatsNewApi {
  constructor(private readonly catalog: CatalogDeps = {}) {}

  async getUpdates(): Promise<ProductUpdate[]> {
    const response = await appHttp.get<unknown>('/blog-api-gateway/product-updates/', AMBIENT_READ)
    return itemsFrom(unwrap(response)).filter(isVisibleWebUpdate).map(toEnglishProductUpdate)
  }

  /** Global, workspace-independent content — read from the versioned `/catalog/` mirror and
   *  cached across sessions (contract 29 §6.6); the legacy endpoint stays the cold-miss source. */
  async getRoadmap(): Promise<RoadmapItem[]> {
    const outcome = await ensureCatalogEntry(ROADMAP_CATALOG, isRoadmapCatalogBody, this.catalog)
    if (outcome.status === 'ready') return outcome.body.items

    const response = await appHttp.get<LegacyRoadmapResponse>(
      '/generic/get_configurations_customization?key=product_roadmap',
      AMBIENT_READ,
    )
    return unwrap(response).data?.[0]?.value ?? []
  }

  /** Invalidates the cached catalog entry after a successful vote, so the forced reload that
   *  reconciles the optimistic adjust reads server truth instead of the pre-vote body (contract
   *  29 §6.6). */
  async toggleRoadmapVote(
    accountId: number,
    workspaceId: number,
    roadmapId: number,
  ): Promise<void> {
    await appHttp.post(
      `/accounts/${accountId}/workspace/configurations/product-roadmap/toggle-vote/`,
      { roadmap_id: roadmapId, workspace_id: workspaceId },
      AMBIENT_READ,
    )
    invalidateCatalogEntry(ROADMAP_CATALOG)
  }
}
